import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  CircleNotch,
  Headset,
  Plus,
  X,
  Warning,
  CheckCircle,
  ArrowsClockwise,
  Bug,
} from '@phosphor-icons/react';
import { platformApi, getPlatformApiErrorMessage } from '@/lib/platformApi';
import {
  SupportConversationButton,
  SupportConversationDrawer,
} from '@/components/platform/SupportConversation';
import { AssigneeLabel, type Assignee } from '@/components/platform/SupportAssignee';
import {
  SupportStatusBadge,
  SUPPORT_ACTION_TONE,
} from '@/components/platform/SupportLifecycleControls';
import { primarySupportAction } from '@/lib/supportLifecycle';
import { TenantSelect } from '@/components/platform/TenantSelect';
import { toast } from 'sonner';
import { ScrollLock } from '@/components/shared/ScrollLock';

const PRIORITIES = ['low', 'normal', 'high', 'critical'] as const;
const CATEGORIES = ['billing', 'technical', 'data_recovery', 'security', 'other'] as const;

// ── Types ─────────────────────────────────────────────────────────────────────

interface Ticket {
  id: string;
  title: string;
  body: string;
  status: string;
  priority: string;
  category: string;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** WHO reported it — the first question of any support conversation. */
  requesterUserId: string | null;
  requesterName: string | null;
  requesterEmail: string | null;
  requesterRole: string | null;
  /** Set when the ticket was raised from a real, observed failure. */
  reportedErrorCode: string | null;
  chambers: { id: string; name: string; slug: string } | null;
  /** The Support executive who owns it. Null until somebody picks it up. */
  assignee: Assignee | null;
  /** Non-internal messages on this ticket. */
  _count?: { messages: number };
  /**
   * Customer messages that have arrived since Support's last reply. Computed once, server-side
   * (conversationSignals in support.service.ts) — the customer's list reads the same fact from
   * the other end, so the two sides can never disagree.
   */
  unansweredCount?: number;
}

function roleLabel(r: string | null) {
  if (!r) return null;
  return r.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

interface TicketsResponse {
  data: Ticket[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

interface TicketSummary {
  byStatus: { status: string; count: number }[];
  byPriority: { priority: string; count: number }[];
  openCount: number;
  criticalCount: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(str: string) {
  return new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function PriorityBadge({ priority }: { priority: string }) {
  const map: Record<string, string> = {
    low: 'bg-plat-neutral-soft text-plat-secondary',
    normal: 'bg-plat-info-soft text-plat-info-text',
    high: 'bg-plat-warning-soft text-plat-warning-text',
    critical: 'bg-plat-danger-soft text-plat-danger-text',
  };
  return <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium capitalize ${map[priority] ?? 'bg-plat-neutral-soft text-plat-secondary'}`}>{priority}</span>;
}

// ── Create ticket modal ───────────────────────────────────────────────────────

function CreateTicketModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState('normal');
  const [category, setCategory] = useState('technical');
  const [chambersId, setChambersId] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      platformApi.post('/support', {
        title, body, priority, category,
        chambersId: chambersId || undefined,
      }),
    onSuccess: () => { toast.success('Ticket created'); onSaved(); onClose(); },
    onError: (err) => toast.error(getPlatformApiErrorMessage(err)),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-plat-scrim/60 px-4">
      <ScrollLock />
      <div className="my-auto w-full max-w-lg rounded-xl border border-plat-border bg-plat-surface p-6 shadow-2xl">
        <div className="mb-5 flex items-center justify-between">
          <h3 className="text-[15px] font-semibold text-plat-fg">New support ticket</h3>
          <button onClick={onClose} className="text-plat-muted hover:text-plat-secondary"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-plat-secondary">Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className="h-9 w-full rounded-lg border border-plat-border bg-plat-bg px-3 text-sm text-plat-fg placeholder-plat-faint focus:border-plat-focus focus:outline-none" placeholder="Brief description of the issue" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-plat-secondary">Category</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="h-9 w-full rounded-lg border border-plat-border bg-plat-bg px-3 text-sm text-plat-secondary focus:outline-none">
                {['billing', 'technical', 'data_recovery', 'security', 'other'].map((c) => (
                  <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-plat-secondary">Priority</label>
              <select value={priority} onChange={(e) => setPriority(e.target.value)} className="h-9 w-full rounded-lg border border-plat-border bg-plat-bg px-3 text-sm text-plat-secondary focus:outline-none">
                {['low', 'normal', 'high', 'critical'].map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-plat-secondary">Organisation (optional)</label>
            <TenantSelect value={chambersId} onChange={setChambersId} includeAll allLabel="Platform (no organisation)" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-plat-secondary">Details</label>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} className="w-full rounded-lg border border-plat-border bg-plat-bg p-3 text-sm text-plat-fg placeholder-plat-faint focus:border-plat-focus focus:outline-none" placeholder="Full description of the issue…" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="rounded-lg border border-plat-border px-4 py-2 text-sm text-plat-secondary hover:bg-plat-hover">Cancel</button>
            <button onClick={() => mutation.mutate()} disabled={!title || !body || mutation.isPending} className="rounded-lg bg-plat-accent-solid px-4 py-2 text-sm font-medium text-plat-on-accent hover:bg-plat-accent-solid-hover disabled:bg-plat-neutral-soft disabled:cursor-not-allowed disabled:text-plat-disabled">
              {mutation.isPending ? <CircleNotch className="h-4 w-4 animate-spin" /> : 'Create ticket'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

export default function PlatformSupportPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  /** The ticket whose conversation is open in the drawer. Communication is never a page of its
   *  own — it stays attached to exactly one ticket. */
  const [talkingTo, setTalkingTo] = useState<Ticket | null>(null);

  const summaryQ = useQuery<TicketSummary>({
    queryKey: ['platform', 'support', 'summary'],
    queryFn: async () => {
      const res = await platformApi.get('/support/summary');
      return res.data?.data ?? res.data;
    },
  });

  const listQ = useQuery<TicketsResponse>({
    queryKey: ['platform', 'support', status, priorityFilter, categoryFilter, page],
    queryFn: async () => {
      const res = await platformApi.get('/support', {
        params: {
          ...(status && { status }),
          ...(priorityFilter && { priority: priorityFilter }),
          ...(categoryFilter && { category: categoryFilter }),
          page, limit: 25,
        },
      });
      return res.data;
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { status?: string; priority?: string } }) =>
      platformApi.patch(`/support/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform', 'support'] });
    },
    onError: (err) => {
      // The server refused the transition — this row is stale because someone else moved the
      // ticket. Report it and refetch so the row shows the action that IS available. No retry.
      toast.error(getPlatformApiErrorMessage(err));
      queryClient.invalidateQueries({ queryKey: ['platform', 'support'] });
    },
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-plat-fg">Support Tickets</h2>
          <p className="mt-0.5 text-sm text-plat-muted">Platform support and issue tracking</p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-plat-accent-solid px-4 py-2 text-sm font-medium text-plat-on-accent hover:bg-plat-accent-solid-hover"
        >
          <Plus className="h-4 w-4" />
          New ticket
        </button>
      </div>

      {/* Summary cards */}
      {summaryQ.data && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[
            { label: 'Open', value: summaryQ.data.openCount, icon: <Headset className="h-5 w-5" />, accent: 'text-plat-info-text' },
            { label: 'Critical', value: summaryQ.data.criticalCount, icon: <Warning className="h-5 w-5" weight="fill" />, accent: summaryQ.data.criticalCount > 0 ? 'text-plat-danger-text' : 'text-plat-faint' },
            { label: 'In Progress', value: summaryQ.data.byStatus.find((s) => s.status === 'in_progress')?.count ?? 0, icon: <ArrowsClockwise className="h-5 w-5" />, accent: 'text-plat-warning-text' },
            { label: 'Resolved', value: summaryQ.data.byStatus.find((s) => s.status === 'resolved')?.count ?? 0, icon: <CheckCircle className="h-5 w-5" weight="fill" />, accent: 'text-plat-success-text' },
          ].map(({ label, value, icon, accent }) => (
            <div key={label} className="rounded-xl border border-plat-border bg-plat-surface p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.08em] text-plat-muted">{label}</div>
                  <div className="mt-2 text-[28px] font-bold leading-none text-plat-fg">{value}</div>
                </div>
                <div className={`mt-1 ${accent}`}>{icon}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => { setStatus(f.value); setPage(1); }}
              className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition ${
                status === f.value
                  ? 'border-plat-accent-solid bg-plat-info-soft text-plat-info-text'
                  : 'border-plat-border text-plat-muted hover:border-plat-border-strong hover:text-plat-secondary'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <select
            value={priorityFilter}
            onChange={(e) => { setPriorityFilter(e.target.value); setPage(1); }}
            className="h-8 rounded-lg border border-plat-border bg-plat-bg px-2.5 text-xs capitalize text-plat-secondary focus:border-plat-focus focus:outline-none"
          >
            <option value="">All priorities</option>
            {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select
            value={categoryFilter}
            onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
            className="h-8 rounded-lg border border-plat-border bg-plat-bg px-2.5 text-xs capitalize text-plat-secondary focus:border-plat-focus focus:outline-none"
          >
            <option value="">All categories</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
          </select>
        </div>
      </div>

      {/* Tickets table */}
      {listQ.isLoading ? (
        <div className="flex items-center justify-center py-16"><CircleNotch className="h-7 w-7 animate-spin text-plat-info-text" /></div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-plat-border bg-plat-surface">
          {(listQ.data?.data ?? []).length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-14 text-center">
              <Headset className="h-8 w-8 text-plat-faint" />
              <p className="text-sm text-plat-muted">No tickets match the current filter.</p>
            </div>
          ) : (
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-plat-border">
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Ticket</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Requester</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Organisation</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Category</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Priority</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Status</th>
                  <th className="hidden px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted xl:table-cell">Assigned</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Created</th>
                  <th className="hidden px-3 py-3.5 text-center text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted lg:table-cell">
                    Comms
                  </th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-plat-border">
                {(listQ.data?.data ?? []).map((ticket) => (
                  <tr key={ticket.id} className="hover:bg-plat-hover">
                    <td className="px-5 py-3.5">
                      {/* The whole ticket is now reachable. Previously the body was rendered as
                          `.slice(0, 80)` here and NOWHERE else, so a 10,000-character customer
                          report was effectively write-only. */}
                      <button
                        onClick={() => navigate(`/platform/support/${ticket.id}`)}
                        className="text-left font-medium text-plat-fg hover:underline"
                      >
                        {ticket.title}
                      </button>
                      <div className="mt-0.5 max-w-[240px] truncate text-xs text-plat-muted">{ticket.body.slice(0, 80)}</div>
                      {ticket.reportedErrorCode && (
                        <div className="mt-1 inline-flex items-center gap-1 rounded bg-plat-danger-soft px-1.5 py-0.5 font-mono text-[10px] text-plat-danger-text">
                          <Bug className="h-3 w-3" />{ticket.reportedErrorCode}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      {ticket.requesterUserId ? (
                        <>
                          <div className="text-plat-fg">{ticket.requesterName ?? ticket.requesterEmail}</div>
                          <div className="text-xs text-plat-muted">{roleLabel(ticket.requesterRole)}</div>
                        </>
                      ) : (
                        <span className="text-xs text-plat-faint">Opened by support</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-plat-secondary">{ticket.chambers?.name ?? <span className="text-plat-faint">Platform</span>}</td>
                    <td className="px-5 py-3.5">
                      <span className="inline-flex rounded-md bg-plat-border px-2 py-0.5 text-xs capitalize text-plat-secondary">
                        {ticket.category.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-5 py-3.5"><PriorityBadge priority={ticket.priority} /></td>
                    <td className="px-5 py-3.5"><SupportStatusBadge status={ticket.status} /></td>
                    {/* OWNERSHIP — one glance answers "is anyone on this?". Dropped below xl
                        rather than squeezed; the full control lives on the ticket itself. */}
                    <td className="hidden px-5 py-3.5 text-xs xl:table-cell">
                      <AssigneeLabel assignee={ticket.assignee} />
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap text-plat-muted">{fmt(ticket.createdAt)}</td>
                    {/* COMMUNICATION — deliberately its own column and its own action. Talking to
                        the requester is not investigating the fault and is not moving the ticket
                        through its workflow; merging them would hide one behind the other. */}
                    <td className="hidden px-3 py-3.5 text-center lg:table-cell">
                      <SupportConversationButton
                        unanswered={ticket.unansweredCount ?? 0}
                        messageCount={ticket._count?.messages ?? 0}
                        onClick={() => setTalkingTo(ticket)}
                      />
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-3">
                        {/* Below lg the dedicated column is dropped rather than squeezed, so the
                            same control moves into the row's action area. It is never absent. */}
                        <SupportConversationButton
                          className="lg:hidden"
                          unanswered={ticket.unansweredCount ?? 0}
                          messageCount={ticket._count?.messages ?? 0}
                          onClick={() => setTalkingTo(ticket)}
                        />
                        {/* ONE lifecycle action per row — the expected next step, from the same
                            table the ticket page uses (lib/supportLifecycle.ts). A `resolved`
                            row used to render Close AND Reopen as two identical links with no
                            hierarchy, which made a triage queue something you had to read rather
                            than move. The full set, including the way back, is on the ticket.
                            `closed` has no forward step, so it offers Reopen. */}
                        {(() => {
                          const action = primarySupportAction(ticket.status);
                          if (!action) return null;
                          return (
                            <button
                              onClick={() => updateMutation.mutate({ id: ticket.id, body: { status: action.to } })}
                              disabled={updateMutation.isPending}
                              className={`text-xs font-medium hover:text-plat-fg disabled:cursor-not-allowed disabled:text-plat-disabled ${SUPPORT_ACTION_TONE[action.id]}`}
                            >
                              {action.label}
                            </button>
                          );
                        })()}
                        <select
                          value={ticket.priority}
                          onChange={(e) => updateMutation.mutate({ id: ticket.id, body: { priority: e.target.value } })}
                          title="Change priority"
                          className="rounded-md border border-plat-border bg-plat-bg px-1.5 py-1 text-xs capitalize text-plat-secondary focus:border-plat-focus focus:outline-none"
                        >
                          {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {(listQ.data?.pagination?.totalPages ?? 0) > 1 && (
        <div className="flex items-center justify-between text-sm text-plat-muted">
          <span>Page {page} of {listQ.data?.pagination.totalPages}</span>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg border border-plat-border px-3 py-1.5 text-xs text-plat-secondary hover:bg-plat-hover disabled:cursor-not-allowed disabled:text-plat-disabled">Previous</button>
            <button disabled={page >= (listQ.data?.pagination.totalPages ?? 1)} onClick={() => setPage((p) => p + 1)} className="rounded-lg border border-plat-border px-3 py-1.5 text-xs text-plat-secondary hover:bg-plat-hover disabled:cursor-not-allowed disabled:text-plat-disabled">Next</button>
          </div>
        </div>
      )}

      {talkingTo && (
        <SupportConversationDrawer
          ticketId={talkingTo.id}
          title={talkingTo.title}
          requester={talkingTo.requesterName ?? talkingTo.requesterEmail}
          onClose={() => setTalkingTo(null)}
        />
      )}

      {createOpen && (
        <CreateTicketModal
          onClose={() => setCreateOpen(false)}
          onSaved={() => queryClient.invalidateQueries({ queryKey: ['platform', 'support'] })}
        />
      )}
    </div>
  );
}
